import Breadcrumbs from "./Breadcrumbs";
import SectionHeading from "./SectionHeading";

export interface PolicySection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
  table?: { headers: string[]; rows: string[][] };
}

export interface PolicyContent {
  title: string;
  updated: string;
  intro: string[];
  sections: PolicySection[];
}

// Shared render for every legal/compliance page (privacy, shipping & payment,
// refund policy, terms) — each page only supplies its own bilingual content
// object, matching the shape PrivacyPolicyPage established first.
export default function PolicyPage({ content }: { content: PolicyContent }) {
  return (
    <section className="bg-white pb-24 pt-24 md:pb-32 md:pt-32">
      <Breadcrumbs items={[{ label: content.title }]} />
      <div className="container-industrial pt-16">
        <SectionHeading title={content.title} description={content.updated} />
        <div className="mt-12 flex max-w-3xl flex-col gap-10">
          {content.intro.map((paragraph) => (
            <p key={paragraph} className="text-sm leading-relaxed text-steel-600">
              {paragraph}
            </p>
          ))}
          {content.sections.map((section) => (
            <div key={section.heading}>
              <h2 className="font-sans text-xl font-semibold tracking-tight text-ink">{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-3 text-sm leading-relaxed text-steel-600">
                  {paragraph}
                </p>
              ))}
              {section.list && (
                <ul className="mt-3 flex flex-col gap-2">
                  {section.list.map((item) => (
                    <li key={item} className="flex gap-2 text-sm leading-relaxed text-steel-600">
                      <span className="text-accent">—</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {section.table && (
                <div className="mt-4 overflow-x-auto border border-steel-200">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-steel-200 bg-steel-50">
                        {section.table.headers.map((header) => (
                          <th key={header} className="px-4 py-2 font-semibold text-ink">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row) => (
                        <tr key={row[0]} className="border-b border-steel-100 last:border-0">
                          {row.map((cell, i) => (
                            <td key={i} className="px-4 py-3 align-top text-steel-600">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
